import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, [selectedTag]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const url = selectedTag 
        ? `${BACKEND_URL}/api/blog?tag=${selectedTag}`
        : `${BACKEND_URL}/api/blog`;
      
      const response = await axios.get(url);
      setPosts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Fehler beim Laden der Beiträge');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get all unique tags
  const allTags = [...new Set(posts.flatMap(post => post.tags))];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Blog – Insights für Handelsimmobilien | RetailInvest"
        description="Expertenwissen zu Handelsimmobilien: Verkaufsstrategien, WAULT-Optimierung, Marktanalysen und mehr. Von spezialisierten Maklern für Handelsimmobilien."
        url="/blog"
        keywords="handelsimmobilien blog, makler insights, immobilien verkaufen, wault optimierung"
      />
      <Navbar />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
              RetailInvest Blog
            </h1>
            <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--gray-700)' }}>
              Expertenwissen und Insights für erfolgreichen Verkauf von Handelsimmobilien
            </p>
          </div>

          {/* Tag Filter */}
          {allTags.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-2 justify-center" data-testid="blog-tag-filter">
              <Badge
                className={`cursor-pointer px-4 py-2 ${!selectedTag ? 'bg-[#2563A0] text-white' : 'bg-white text-[#2563A0] border border-[#2563A0]'}`}
                onClick={() => setSelectedTag(null)}
                data-testid="tag-all"
              >
                Alle Themen
              </Badge>
              {allTags.map(tag => (
                <Badge
                  key={tag}
                  className={`cursor-pointer px-4 py-2 ${selectedTag === tag ? 'bg-[#2563A0] text-white' : 'bg-white text-[#2563A0] border border-[#2563A0]'}`}
                  onClick={() => setSelectedTag(tag)}
                  data-testid={`tag-${tag.toLowerCase()}`}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card key={i} className="p-6">
                  <Skeleton className="h-48 w-full mb-4" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </Card>
              ))}
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <Card className="p-12 text-center" data-testid="blog-error">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                {error}
              </h3>
              <button
                onClick={fetchPosts}
                className="px-6 py-3 bg-[#2563A0] text-white rounded-lg hover:bg-[#1B4D7A] transition-colors"
                data-testid="retry-button"
              >
                Erneut versuchen
              </button>
            </Card>
          )}

          {/* Empty State */}
          {!loading && !error && posts.length === 0 && (
            <Card className="p-12 text-center" data-testid="blog-empty">
              <Search className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--gray-400)' }} />
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                Keine Beiträge gefunden
              </h3>
              <p style={{ color: 'var(--gray-600)' }}>
                {selectedTag 
                  ? `Keine Beiträge mit dem Tag "${selectedTag}" gefunden.`
                  : 'Derzeit sind keine Beiträge verfügbar.'
                }
              </p>
              {selectedTag && (
                <button
                  onClick={() => setSelectedTag(null)}
                  className="mt-4 px-6 py-3 bg-[#2563A0] text-white rounded-lg hover:bg-[#1B4D7A] transition-colors"
                >
                  Alle Beiträge anzeigen
                </button>
              )}
            </Card>
          )}

          {/* Blog Posts Grid */}
          {!loading && !error && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-posts-grid">
              {posts.map(post => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <Card className="h-full overflow-hidden hover:shadow-2xl transition-all duration-300">
                    {/* Hero Image */}
                    {post.hero_image_url && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={post.hero_image_url}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 to-transparent"></div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-6">
                      {/* Meta */}
                      <div className="flex items-center gap-4 mb-3 text-sm" style={{ color: 'var(--gray-500)' }}>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(post.published_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          5 Min
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-[#D4A853] transition-colors" style={{ color: 'var(--navy-800)' }}>
                        {post.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-base mb-4 line-clamp-3" style={{ color: 'var(--gray-700)' }}>
                        {post.excerpt}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map(tag => (
                          <Badge
                            key={tag}
                            className="bg-[#EFF6FF] text-[#2563A0] text-xs"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      {/* Read More */}
                      <div className="flex items-center gap-2 text-[#2563A0] font-semibold group-hover:gap-4 transition-all">
                        <span>Weiterlesen</span>
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
